import { Injectable } from "@angular/core";

/**
 * Owns the CAIRA login session.
 *
 * IMPORTANT: CAIRA has its own session, completely separate from the base CPA
 * LMS login. The base login stores its token under environment.authTokenKey +
 * `islogged_cpa`; if CAIRA reused those keys it would overwrite the base
 * session (and vice-versa) whenever the user toggles between CAIRA and the
 * dashboard / courses / gameplan sections. So every CAIRA value lives under its
 * own `caira_*` keys and never touches the base ones.
 */

@Injectable({
  providedIn: "root",
})
export class CairaAuthService {
  private readonly ACCESS_TOKEN = "caira_access_token";
  private readonly REFRESH_TOKEN = "caira_refresh_token";
  private readonly USER_ID = "caira_user_id";
  private readonly SESSION = "caira_session";
  private readonly LOGGED_FLAG = "islogged_caira";

  /** Persist the decrypted QR-login payload under CAIRA-only keys. */
  storeSession(tokens: any): boolean {
    if (!tokens) return false;

    // QR payload uses `access_token`; the web email/phone endpoints use `token`.
    const accessToken = tokens.access_token || tokens.token;
    if (!accessToken) return false;

    const userId = tokens.user_id != null ? tokens.user_id : tokens.user?.id;

    localStorage.setItem(this.ACCESS_TOKEN, accessToken);
    localStorage.setItem(this.LOGGED_FLAG, "1");
    if (tokens.refresh_token) {
      localStorage.setItem(this.REFRESH_TOKEN, tokens.refresh_token);
    }
    if (userId != null) {
      localStorage.setItem(this.USER_ID, `${userId}`);
    }
    // Keep the full payload too, for anything else CAIRA needs.
    localStorage.setItem(this.SESSION, JSON.stringify(tokens));
    return true;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  getUserId(): string | null {
    return localStorage.getItem(this.USER_ID);
  }

  /** True when a CAIRA session exists and its access token hasn't expired. */
  isLoggedIn(): boolean {
    const token = this.getAccessToken();
    if (!token || localStorage.getItem(this.LOGGED_FLAG) !== "1") {
      return false;
    }
    return !this.isJwtExpired(token);
  }

  /** Clear the CAIRA session only — leaves the base CPA session untouched. */
  logout(): void {
    [
      this.ACCESS_TOKEN,
      this.REFRESH_TOKEN,
      this.USER_ID,
      this.SESSION,
      this.LOGGED_FLAG,
    ].forEach((key) => localStorage.removeItem(key));
  }

  /** Decode the JWT `exp` claim and check it against now. Treats anything it
   *  can't parse as expired, so a bad token falls back to the QR login. */
  private isJwtExpired(token: string): boolean {
    try {
      const part = token.split(".")[1];
      const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
      const padded =
        b64.length % 4 ? b64 + "=".repeat(4 - (b64.length % 4)) : b64;
      const payload = JSON.parse(atob(padded));
      if (!payload?.exp) return false; // no expiry claim → treat as valid
      return payload.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }
}
